import { useParams, Link } from 'react-router-dom';
import { useState, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const scrollRef = useRef<HTMLDivElement>(null);
  const article = articles.find((a) => a.id === Number(id));
  const relatedArticles = articles
    .filter((a) => a.id !== Number(id))
    .slice(0, 4);

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-16 text-center">
          <h1 className="text-2xl font-bold text-foreground-950">文章不存在</h1>
          <Link to="/" className="text-accent-500 hover:text-accent-600 mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: `${article.publishDate}T${article.publishTime}:00+08:00`,
    publisher: {
      '@type': 'Organization',
      name: '51爆料网',
    },
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: article.category, href: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="px-4 md:px-6 lg:px-8 py-4">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <article className="flex flex-col lg:flex-row">
            <div className="lg:w-3/5 px-4 md:px-6 lg:px-8 py-6 lg:py-10">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-2 h-2 rounded-full bg-foreground-950"></span>
                <span className="text-xs text-foreground-600 font-medium tracking-wide uppercase">
                  独家爆料
                </span>
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                  {article.publishDate} {article.publishTime}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                  {article.readCount} 阅读
                </span>
                <span className="px-2 py-0.5 rounded-md text-xs bg-accent-100 text-accent-700 font-medium whitespace-nowrap">
                  {article.category}
                </span>
              </div>

              <div className="mt-8 mb-10">
                <div className="text-lg text-foreground-800 font-medium leading-relaxed border-l-4 border-accent-500 pl-5 py-2 italic">
                  &ldquo;{article.excerpt}&rdquo;
                </div>
              </div>

              <div
                className="prose prose-base max-w-none text-foreground-800 leading-relaxed article-content"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 grid grid-cols-3 gap-4 border-t border-b border-background-200/70 py-6">
                <div className="text-center">
                  <p className="text-2xl md:text-3xl font-black text-accent-500">
                    {article.readCount}+
                  </p>
                  <p className="text-xs text-foreground-500 mt-1">阅读量</p>
                </div>
                <div className="text-center border-x border-background-200/70">
                  <p className="text-2xl md:text-3xl font-black text-accent-500">
                    {article.hotIndex}+
                  </p>
                  <p className="text-xs text-foreground-500 mt-1">热度指数</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl md:text-3xl font-black text-accent-500">
                    99+
                  </p>
                  <p className="text-xs text-foreground-500 mt-1">网友讨论</p>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <span className="text-sm text-foreground-700 font-medium">分享到：</span>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-accent-100 hover:text-accent-600 transition-colors">
                  <i className="ri-weibo-line w-4 h-4 flex items-center justify-center"></i>
                </a>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-accent-100 hover:text-accent-600 transition-colors">
                  <i className="ri-wechat-line w-4 h-4 flex items-center justify-center"></i>
                </a>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-accent-100 hover:text-accent-600 transition-colors">
                  <i className="ri-share-forward-line w-4 h-4 flex items-center justify-center"></i>
                </a>
              </div>

              <div className="mt-10 flex items-center justify-between">
                <Link
                  to={article.id > 1 ? `/article/${article.id - 1}` : '#'}
                  className={`flex items-center gap-1 text-sm font-medium transition-colors whitespace-nowrap ${
                    article.id > 1
                      ? 'text-foreground-700 hover:text-accent-500'
                      : 'text-foreground-300 pointer-events-none'
                  }`}
                >
                  <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                  上一篇
                </Link>
                <Link
                  to={article.id < articles.length ? `/article/${article.id + 1}` : '#'}
                  className={`flex items-center gap-1 text-sm font-medium transition-colors whitespace-nowrap ${
                    article.id < articles.length
                      ? 'text-foreground-700 hover:text-accent-500'
                      : 'text-foreground-300 pointer-events-none'
                  }`}
                >
                  下一篇
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </Link>
              </div>
            </div>

            <div className="lg:w-2/5 relative min-h-[400px] lg:min-h-screen lg:sticky lg:top-16">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent lg:bg-gradient-to-l lg:from-black/20 lg:to-transparent"></div>
              <div className="absolute bottom-6 left-6 lg:bottom-10 lg:left-8">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-medium">
                  <i className="ri-verified-badge-line w-3.5 h-3.5 flex items-center justify-center"></i>
                  深度独家
                </span>
              </div>
            </div>
          </article>

          <section className="py-14 md:py-16 px-4 md:px-6 lg:px-8 bg-background-100/50">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950">
                  看了又看
                </h2>
                <p className="text-sm text-foreground-500 mt-1">同类热门爆料推荐</p>
              </div>
            </div>

            <div className="relative">
              <button
                onClick={scrollLeft}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-background-50 border border-background-200 text-foreground-700 hover:bg-background-100 hover:border-background-300 transition-all"
              >
                <i className="ri-arrow-left-s-line w-4 h-4 flex items-center justify-center"></i>
              </button>

              <div
                ref={scrollRef}
                className="flex gap-5 overflow-x-auto scrollbar-hide pb-2 px-1"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {relatedArticles.map((a) => (
                  <ArticleCard
                    key={a.id}
                    id={a.id}
                    title={a.title}
                    excerpt={a.excerpt}
                    category={a.category}
                    categorySlug={a.categorySlug}
                    hotIndex={a.hotIndex}
                    readCount={a.readCount}
                    imageUrl={a.imageUrl}
                  />
                ))}
              </div>

              <button
                onClick={scrollRight}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-background-50 border border-background-200 text-foreground-700 hover:bg-background-100 hover:border-background-300 transition-all"
              >
                <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
              </button>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}